import { computed, inject, Injectable, signal } from '@angular/core';

import { UserScope } from '@core/interfaces';

import { PermissionService } from './permission.service';

const defaultUserScope: UserScope = 'direct_reports';
const validUserScopes: UserScope[] = ['all', 'direct_reports', 'hierarchy'];

@Injectable({ providedIn: 'root' })
export class UserScopeService {
  private readonly storageKeyPrefix = 'userScope';
  private readonly permissionService = inject(PermissionService);
  private readonly selectedScope$ = signal<UserScope>(defaultUserScope);
  private userStorageKey: string | null = null;

  readonly availableScopes = this.permissionService.availableUserScopes;
  readonly selectedScope = this.selectedScope$.asReadonly();
  readonly effectiveScope = computed<UserScope | null>(() => {
    const selectedScope = this.selectedScope$();
    return this.availableScopes().includes(selectedScope) ? selectedScope : null;
  });

  initialize(userId: string): void {
    this.userStorageKey = `${this.storageKeyPrefix}:${userId}`;
    const storedScope = localStorage.getItem(this.userStorageKey);
    const selectedScope = this.isUserScope(storedScope) ? storedScope : defaultUserScope;
    const normalizedScope = this.availableScopes().includes(selectedScope)
      ? selectedScope
      : defaultUserScope;

    this.selectedScope$.set(normalizedScope);

    if (this.availableScopes().includes(normalizedScope)) {
      localStorage.setItem(this.userStorageKey, normalizedScope);
    }
  }

  setScope(userScope: UserScope): void {
    if (!this.availableScopes().includes(userScope)) return;

    this.selectedScope$.set(userScope);

    if (this.userStorageKey) {
      localStorage.setItem(this.userStorageKey, userScope);
    }
  }

  private isUserScope(value: string | null): value is UserScope {
    return value !== null && validUserScopes.includes(value as UserScope);
  }
}
